/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import swal from "sweetalert";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import { alpha } from "@mui/material/styles";
// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlaceIcon from "@mui/icons-material/Place";
import LinkIcon from "@mui/icons-material/Link";
import { QRCodeSVG } from "qrcode.react";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

applyPlugin(jsPDF);

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Helper load logo Rote Ndao untuk PDF
async function loadLogoBase64() {
  try {
    const logoUrl = `${process.env.PUBLIC_URL || ""}/logorote.png`;
    const res = await fetch(logoUrl, { credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Helper tambah logo di pojok kanan atas PDF
function addLogoToPdf(doc, logoBase64) {
  if (!logoBase64) return;
  try {
    const pageW = doc.internal.pageSize.getWidth();
    const logoW = 12;
    const logoH = 12;
    const x = pageW - logoW - 14;
    const y = 10;
    doc.addImage(logoBase64, "PNG", x, y, logoW, logoH);
  } catch (e) {
    console.warn("Logo add failed:", e);
  }
}

function EventYangDiikuti() {
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [filteredEventsList, setFilteredEventsList] = useState([]);
  const [uid_RSS, setUid_RSS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // State untuk dialog view notulen/foto
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDetailEvent, setViewingDetailEvent] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // State untuk dialog daftar peserta
  const [openPesertaDialog, setOpenPesertaDialog] = useState(false);
  const [daftarPeserta, setDaftarPeserta] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [selectedDetailEventId, setSelectedDetailEventId] = useState(null);
  const [downloadingPesertaPdf, setDownloadingPesertaPdf] = useState(false);

  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDialogEvent, setQrDialogEvent] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUid_RSS(user.uid_RSS);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    const fetchDetailEvents = async () => {
      if (!uid_RSS) {
        console.log("uid_RSS belum tersedia, menunggu...");
        return;
      }

      console.log("Fetching attended events dengan uid_RSS:", uid_RSS);
      setLoading(true);
      try {
        let url = `${API_ESA_OFFICE}events/attended?uid_RSS=${uid_RSS}`;
        if (tahun) {
          url += `&tahun=${tahun}`;
        }
        console.log("Request URL:", url);
        const response = await axios.get(url);
        console.log("Response data:", response.data);
        setDetailEventsList(response.data || []);
      } catch (error) {
        console.error("Error fetching attended events:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        swal({
          title: "Error",
          text:
            error.response?.data?.message ||
            "Gagal mengambil data event yang diikuti",
          icon: "error",
        });
        setDetailEventsList([]);
      } finally {
        setLoading(false);
      }
    };

    console.log("useEffect triggered, uid_RSS:", uid_RSS, "tahun:", tahun);
    if (uid_RSS) {
      fetchDetailEvents();
    }
  }, [uid_RSS, tahun]);

  // Filter berdasarkan search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEventsList(detailEventsList);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = detailEventsList.filter((event) => {
      const kegiatan = (event.kegiatan || "").toLowerCase();
      const detailKegiatan = (event.detail_kegiatan || "").toLowerCase();
      const tempat = (event.tempat || "").toLowerCase();

      return (
        kegiatan.includes(query) ||
        detailKegiatan.includes(query) ||
        tempat.includes(query)
      );
    });

    setFilteredEventsList(filtered);
  }, [searchQuery, detailEventsList]);

  /** Terbaru → terlama berdasarkan waktu mulai sesi */
  const sortedFilteredEvents = useMemo(() => {
    return [...filteredEventsList].sort((a, b) => {
      const ta = a.waktu_mulai ? new Date(a.waktu_mulai).getTime() : 0;
      const tb = b.waktu_mulai ? new Date(b.waktu_mulai).getTime() : 0;
      return tb - ta;
    });
  }, [filteredEventsList]);

  /** Rekapan berdasarkan data tahun terpilih (bukan filter pencarian) */
  const attendedStats = useMemo(() => {
    const rows = detailEventsList;
    const total = rows.length;
    const rutin = rows.filter((e) => String(e.status_event || "").toLowerCase() === "rutin").length;
    const internal = rows.filter((e) => String(e.scope || "").toLowerCase() === "internal").length;
    const external = rows.filter((e) => {
      const s = String(e.scope || "").toLowerCase();
      return s === "pemda" || s === "publik";
    }).length;
    return { total, rutin, internal, external };
  }, [detailEventsList]);

  const generatePDF = async (detailEvent) => {
    setPdfLoading(true);
    // Fetch data peserta
    let pesertaList = [];
    try {
      const pesertaResponse = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEvent.id}`
      );
      pesertaList = pesertaResponse.data || [];
    } catch (error) {
      console.error("Error fetching peserta:", error);
    }
    const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
    const signatureBase64Map = {};
    await Promise.all(
      (pesertaList || []).map(async (p, idx) => {
        const sig = p.signature_file;
        if (!sig || typeof sig !== "string") return;
        const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
        try {
          const res = await fetch(imgUrl, { credentials: "include" });
          if (!res.ok) return;
          const blob = await res.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          signatureBase64Map[idx] = base64;
        } catch {
          // skip jika gagal load gambar
        }
      })
    );
    const doc = new jsPDF();
    const logoBase64 = await loadLogoBase64();
    addLogoToPdf(doc, logoBase64);
    let yPosition = 20;

    // Helper function untuk render HTML dengan mempertahankan formatting
    const renderHtmlContent = (html, x, y, maxWidth) => {
      if (!html) return y;

      let currentY = y;
      const lineHeight = 5;
      const paragraphSpacing = 3;

      // Set default font normal
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Parse HTML
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      // Track current font state
      let currentFontStyle = "normal"; // "normal", "bold", "italic"

      // Process nodes recursively
      const processNode = (node, indent = 0, parentFontStyle = "normal") => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Check if need new page
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // Get computed style untuk text alignment
            const parent = node.parentElement;
            const textAlign =
              parent?.style?.textAlign ||
              window.getComputedStyle(parent || document.body).textAlign ||
              "left";

            // Set font sesuai parent font style
            if (parentFontStyle === "bold") {
              doc.setFont("helvetica", "bold");
            } else if (parentFontStyle === "italic") {
              doc.setFont("helvetica", "italic");
            } else {
              doc.setFont("helvetica", "normal");
            }

            // Split text to fit width
            const lines = doc.splitTextToSize(text, maxWidth - indent * 5);

            // Render each line dengan alignment
            lines.forEach((line) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              let xPos = x + indent * 5;
              if (textAlign === "center") {
                xPos = 105; // Center of page
                doc.text(line, xPos, currentY, { align: "center" });
              } else if (textAlign === "right") {
                xPos = x + maxWidth;
                doc.text(line, xPos, currentY, { align: "right" });
              } else {
                doc.text(line, xPos, currentY);
              }
              currentY += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();

          // Handle different HTML elements
          if (tagName === "p" || tagName === "div") {
            // Add spacing before paragraph (except first)
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing;
            }

            // Process children dengan font style dari parent
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });

            // Add spacing after paragraph
            currentY += paragraphSpacing;
          } else if (tagName === "br") {
            currentY += lineHeight;
          } else if (tagName === "strong" || tagName === "b") {
            // Bold text - pass "bold" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
          } else if (tagName === "em" || tagName === "i") {
            // Italic text - pass "italic" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "italic");
            });
          } else if (tagName === "ul" || tagName === "ol") {
            // List
            Array.from(node.children).forEach((child, index) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              // Set font normal untuk bullet
              doc.setFont("helvetica", "normal");
              const bullet = tagName === "ul" ? "•" : `${index + 1}.`;
              doc.text(bullet, x + indent * 5, currentY);
              processNode(child, indent + 1, parentFontStyle);
            });
          } else if (tagName === "li") {
            // List item
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          } else if (tagName === "h1" || tagName === "h2" || tagName === "h3") {
            // Headings - always bold
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing * 2;
            }
            const fontSize = tagName === "h1" ? 16 : tagName === "h2" ? 14 : 12;
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", "bold");
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            currentY += paragraphSpacing;
          } else {
            // Process other elements - maintain parent font style
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          }
        }
      };

      // Process all nodes dengan font normal sebagai default
      Array.from(tmp.childNodes).forEach((child) => {
        processNode(child, 0, "normal");
      });

      // Reset font ke normal setelah selesai
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      return currentY;
    };

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Event", 105, yPosition, { align: "center" });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${detailEvent.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;
    doc.text(
      `Detail Kegiatan: ${detailEvent.detail_kegiatan || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;
    doc.text(
      `Lokasi Pelaksanaan Rapat: ${detailEvent.tempat || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;

    const waktuMulai = detailEvent.waktu_mulai
      ? new Date(detailEvent.waktu_mulai).toLocaleString("id-ID")
      : "-";
    const waktuFinish = detailEvent.waktu_finish
      ? new Date(detailEvent.waktu_finish).toLocaleString("id-ID")
      : "-";
    doc.text(`Waktu Mulai: ${waktuMulai}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Waktu Selesai: ${waktuFinish}`, 14, yPosition);
    yPosition += 10;

    // Notulen
    if (detailEvent.notulen && detailEvent.notulen.trim() !== "") {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Notulen", 14, yPosition);
      yPosition += 7;

      // Check if need new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Render HTML content dengan mempertahankan formatting
      yPosition = renderHtmlContent(detailEvent.notulen, 14, yPosition, 180);
    }

    // Foto
    if (
      detailEvent.foto &&
      Array.isArray(detailEvent.foto) &&
      detailEvent.foto.length > 0
    ) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Foto Kegiatan (${detailEvent.foto.length})`, 14, yPosition);
      yPosition += 10;

      for (let i = 0; i < detailEvent.foto.length; i++) {
        const fotoUrl = detailEvent.foto[i];
        const fullUrl = fotoUrl.startsWith("/")
          ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
          : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`;

        try {
          // Load image
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = fullUrl;
          });

          // Calculate image dimensions to fit page width
          const maxWidth = 180;
          const maxHeight = 100;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          imgWidth = imgWidth * ratio;
          imgHeight = imgHeight * ratio;

          // Check if need new page
          if (yPosition + imgHeight > 270) {
            doc.addPage();
            yPosition = 20;
          }

          // Add image
          doc.text(`Foto ${i + 1}:`, 14, yPosition);
          yPosition += 5;
          doc.addImage(img, "JPEG", 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

          // Check if need new page after image
          if (yPosition > 250 && i < detailEvent.foto.length - 1) {
            doc.addPage();
            yPosition = 20;
          }
        } catch (error) {
          console.error("Error loading image:", error);
          doc.text(`Foto ${i + 1}: Gagal memuat gambar`, 14, yPosition);
          yPosition += 7;
        }
      }
    }

    // Daftar Peserta (dengan kolom Tanda Tangan dan gambar, sama seperti laporan-event)
    if (pesertaList.length > 0) {
      // Check if need new page
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Daftar Peserta (${pesertaList.length} orang)`, 14, yPosition);
      yPosition += 10;

      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;
      const headers = [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]];
      const body = pesertaList.map((p, idx) => [
        String(idx + 1),
        (p.nama_user || p.nama || "-")
          .replace(/\r\n/g, " ")
          .replace(/\n/g, " ")
          .substring(0, 50),
        (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
        p.waktu ? formatDateTime(p.waktu) : "-",
        signatureBase64Map[idx] ? "" : "-",
      ]);

      doc.autoTable({
        head: headers,
        body,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 36 },
          3: { cellWidth: 34 },
          4: { cellWidth: sigColWidthMm },
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 32));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          const x = cell.x + padding;
          const y = cell.y + padding;
          try {
            doc.addImage(base64, "PNG", x, y, imgW, imgH);
          } catch {
            // fallback jika addImage gagal
          }
        },
      });
      yPosition = doc.lastAutoTable.finalY + 5;
    }

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    setPdfLoading(false);
    return url;
  };

  const handleOpenViewDialog = async (detailEvent) => {
    setViewingDetailEvent(detailEvent);
    setOpenViewDialog(true);
    setPdfLoading(true);

    // Generate PDF
    try {
      const pdfUrl = await generatePDF(detailEvent);
      setPdfBlobUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF:", error);
      swal({
        title: "Error",
        text: "Gagal membuat PDF",
        icon: "error",
      });
      setPdfLoading(false);
    }
  };

  const handleCloseViewDialog = () => {
    // Clean up blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenViewDialog(false);
    setViewingDetailEvent(null);
    setPdfLoading(false);
  };

  const handleDownloadPesertaPDF = useCallback(async () => {
    if (!daftarPeserta?.length) return;

    setDownloadingPesertaPdf(true);
    try {
      const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
      const signatureBase64Map = {};
      await Promise.all(
        daftarPeserta.map(async (p, idx) => {
          const sig = p.signature_file;
          if (!sig || typeof sig !== "string") return;
          const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
          try {
            const res = await fetch(imgUrl, { credentials: "include" });
            if (!res.ok) return;
            const blob = await res.blob();
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            signatureBase64Map[idx] = base64;
          } catch {
            // skip jika gagal load gambar
          }
        })
      );

      const detailEv = detailEventsList.find((d) => d.id === selectedDetailEventId);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [215, 279] });
      const logoBase64 = await loadLogoBase64();
      addLogoToPdf(doc, logoBase64);
      let yPos = 12;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Daftar Peserta Hadir", 14, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Detail Kegiatan: ${detailEv?.detail_kegiatan || "-"}`, 14, yPos);
      yPos += 5;
      const tanggalPelaksanaan = detailEv?.waktu_mulai
        ? new Date(detailEv.waktu_mulai).toLocaleDateString("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "-";
      doc.text(`Tanggal Pelaksanaan: ${tanggalPelaksanaan}`, 14, yPos);
      yPos += 5;
      doc.text(
        `Lokasi Pelaksanaan Rapat: ${detailEv?.tempat || "-"}`,
        14,
        yPos
      );
      yPos += 5;
      doc.text(`Jumlah Peserta Hadir: ${daftarPeserta.length} orang`, 14, yPos);
      yPos += 8;

      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;
      const headers = [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]];
      const body = daftarPeserta.map((p, idx) => [
        String(idx + 1),
        (p.nama_user || p.nama || "")
          .replace(/\r\n/g, " ")
          .replace(/\n/g, " "),
        (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
        p.waktu ? formatDateTime(p.waktu) : "-",
        signatureBase64Map[idx] ? "" : "-",
      ]);

      doc.autoTable({
        head: headers,
        body,
        startY: yPos,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 38 },
          3: { cellWidth: 35 },
          4: { cellWidth: sigColWidthMm },
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 32));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          const x = cell.x + padding;
          const y = cell.y + padding;
          try {
            doc.addImage(base64, "PNG", x, y, imgW, imgH);
          } catch {
            // fallback jika addImage gagal
          }
        },
      });

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const now = new Date();
      const tanggalDownload = now.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const jamDownload = now.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Didownload dari Esa Office Apps tanggal ${tanggalDownload} dan jam ${jamDownload} oleh ${user?.nama || user?.name || "User"}`,
        14,
        doc.internal.pageSize.getHeight() - 10
      );

      const safeTitle = (detailEv?.detail_kegiatan || "daftar-peserta")
        .replace(/[^a-zA-Z0-9-_]/g, "_")
        .substring(0, 50);
      doc.save(`daftar-peserta-${safeTitle}-${selectedDetailEventId}.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      swal("Error", "Gagal export PDF: " + (err?.message || "Unknown error"), "error");
    } finally {
      setDownloadingPesertaPdf(false);
    }
  }, [daftarPeserta, selectedDetailEventId, detailEventsList]);

  const handleOpenPesertaDialog = async (detailEventId, jumlahPeserta) => {
    if (jumlahPeserta === 0) return;

    setSelectedDetailEventId(detailEventId);
    setOpenPesertaDialog(true);
    setLoadingPeserta(true);
    setDaftarPeserta([]);

    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEventId}`
      );
      setDaftarPeserta(response.data || []);
    } catch (error) {
      console.error("Error fetching daftar peserta:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data peserta",
        icon: "error",
      });
      setDaftarPeserta([]);
    } finally {
      setLoadingPeserta(false);
    }
  };

  const handleClosePesertaDialog = () => {
    setOpenPesertaDialog(false);
    setDaftarPeserta([]);
    setSelectedDetailEventId(null);
  };

  const getStatusEventLabel = (status) => {
    switch (status) {
      case "satu_kali":
        return "Satu Kali";
      case "rutin":
        return "Rutin";
      case "evaluasi":
        return "Evaluasi";
      case "asistensi":
        return "Asistensi";
      default:
        return status || "-";
    }
  };

  const getStatusEventColor = (status) => {
    switch (status) {
      case "satu_kali":
        return "info";
      case "rutin":
        return "warning";
      case "evaluasi":
        return "success";
      case "asistensi":
        return "secondary";
      default:
        return "default";
    }
  };

  const getScopeLabel = (scope) => {
    if (!scope) return "-";
    const s = String(scope).toLowerCase();
    if (s === "internal") return "Internal";
    if (s === "pemda") return "Pemda";
    if (s === "publik") return "Publik";
    return scope;
  };

  const getScopeColor = (scope) => {
    if (!scope) return "default";
    const s = String(scope).toLowerCase();
    if (s === "internal") return "primary";
    if (s === "pemda") return "info";
    if (s === "publik") return "success";
    return "default";
  };

  const daftarHadirUrl = (idEvents) =>
    `${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${idEvents}`;

  const accent = {
    blue: "#1565c0",
    blueInternal: "#3949ab",
    green: "#2e7d32",
    red: "#c62828",
    slate: "#546e7a",
  };

  /** Model sama smartDH/daftar-hadir: Chip filled + icon + tegas; warna aksen minimal */
  const rekapChipSx = (bg) => ({
    fontSize: "0.875rem",
    fontWeight: "bold",
    backgroundColor: bg,
    color: "#fff",
    "& .MuiChip-icon": { color: "#fff" },
  });

  const infoAccentSx = (borderColor) => ({
    pl: 1.25,
    pr: 0.75,
    py: 0.4,
    borderLeft: "3px solid",
    borderLeftColor: borderColor,
    bgcolor: alpha(borderColor, 0.06),
    borderRadius: "0 6px 6px 0",
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Event yang Diikuti
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Search */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDInput
                      label="Cari Nama Event, Detail Event, atau Lokasi"
                      variant="standard"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                {!loading && (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="text" mb={2}>
                      Rekap event diikuti — {tahun}
                    </MDTypography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Chip
                          icon={<Icon>group</Icon>}
                          label={`Total: ${attendedStats.total}`}
                          variant="filled"
                          sx={rekapChipSx(accent.blue)}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>event_repeat</Icon>}
                          label={`Rutin: ${attendedStats.rutin}`}
                          variant="filled"
                          sx={rekapChipSx(accent.green)}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>domain</Icon>}
                          label={`Internal: ${attendedStats.internal}`}
                          variant="filled"
                          sx={rekapChipSx(accent.blueInternal)}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>public</Icon>}
                          label={`Eksternal: ${attendedStats.external}`}
                          variant="filled"
                          sx={rekapChipSx(accent.red)}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : sortedFilteredEvents.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                    <MDTypography variant="h6" color="text.secondary">
                      {searchQuery.trim()
                        ? "Tidak ada event yang sesuai pencarian"
                        : "Tidak ada data event yang diikuti"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {searchQuery.trim()
                        ? "Coba ubah kata kunci"
                        : "Belum ada event yang Anda ikuti"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {sortedFilteredEvents.map((detailEvent) => {
                      const jumlahPeserta = detailEvent.jumlah_peserta || 0;
                      const idEvents = detailEvent.id_events;
                      const hasLaporan =
                        (detailEvent.notulen && detailEvent.notulen.trim() !== "") ||
                        (detailEvent.foto && Array.isArray(detailEvent.foto) && detailEvent.foto.length > 0);
                      const qrPayload = idEvents
                        ? {
                            id_events: idEvents,
                            kegiatan: detailEvent.kegiatan,
                            status_event: detailEvent.status_event,
                            scope: detailEvent.scope,
                            jumlah_detail_events: 1,
                            latestTempat: detailEvent.tempat,
                          }
                        : null;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={detailEvent.id}>
                          <Card
                            sx={{
                              p: 2,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                              transition: "box-shadow 0.2s, transform 0.2s",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <MDBox display="flex" flexDirection="column" gap={1.5} sx={{ flex: 1 }}>
                              <MDTypography variant="body2" sx={{ lineHeight: 1.35, fontSize: "0.875rem", fontWeight: 400 }}>
                                {detailEvent.kegiatan || "-"}
                              </MDTypography>
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Chip
                                  label={getStatusEventLabel(detailEvent.status_event)}
                                  color={getStatusEventColor(detailEvent.status_event)}
                                  size="small"
                                  variant="filled"
                                />
                                {detailEvent.scope && (
                                  <Chip
                                    label={getScopeLabel(detailEvent.scope)}
                                    color={getScopeColor(detailEvent.scope)}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                <MDTypography variant="caption" color="text.secondary">
                                  1 sesi
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" flexDirection="column" gap={0.5}>
                                <MDBox sx={infoAccentSx(accent.blue)}>
                                  <MDTypography variant="caption" sx={{ color: accent.blue, fontWeight: 600, fontSize: "0.65rem", display: "block", mb: 0.15 }}>
                                    Terbaru
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                    {formatDateTime(detailEvent.waktu_mulai)}
                                  </MDTypography>
                                </MDBox>
                                <MDBox sx={infoAccentSx(accent.green)}>
                                  <MDTypography variant="caption" sx={{ color: accent.green, fontWeight: 600, fontSize: "0.65rem", display: "block", mb: 0.15 }}>
                                    Selesai
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                    {formatDateTime(detailEvent.waktu_finish)}
                                  </MDTypography>
                                </MDBox>
                                <MDBox sx={infoAccentSx(accent.red)}>
                                  <MDTypography variant="caption" component="div" sx={{ color: accent.red, fontWeight: 600, fontSize: "0.65rem", display: "block", mb: 0.15, lineHeight: 1.35 }}>
                                    Tempat
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    sx={{ display: "block", lineHeight: 1.35, wordBreak: "break-word", m: 0 }}
                                  >
                                    {detailEvent.tempat || "-"}
                                  </MDTypography>
                                </MDBox>
                                {(detailEvent.dinas_peserta_nama || detailEvent.nama_dinas) && (
                                  <MDBox sx={infoAccentSx(accent.slate)}>
                                    <MDTypography variant="caption" component="div" sx={{ color: accent.slate, fontWeight: 600, fontSize: "0.65rem", display: "block", mb: 0.15, lineHeight: 1.35 }}>
                                      Dinas
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      component="div"
                                      sx={{ display: "block", lineHeight: 1.35, wordBreak: "break-word", m: 0 }}
                                    >
                                      {detailEvent.dinas_peserta_nama || detailEvent.nama_dinas}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={infoAccentSx(accent.green)}>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Peserta: <strong>{jumlahPeserta}</strong>
                                  </MDTypography>
                                  <Tooltip title={jumlahPeserta ? "Lihat daftar peserta" : "Tidak ada peserta"}>
                                    <span>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={jumlahPeserta === 0}
                                        onClick={() => handleOpenPesertaDialog(detailEvent.id, jumlahPeserta)}
                                        sx={{
                                          minWidth: 0,
                                          px: 1,
                                          py: 0.125,
                                          fontSize: "0.65rem",
                                          textTransform: "none",
                                          lineHeight: 1.6,
                                          borderColor: alpha(accent.green, 0.45),
                                          color: accent.green,
                                          "&:hover": {
                                            borderColor: accent.green,
                                            bgcolor: alpha(accent.green, 0.06),
                                          },
                                        }}
                                      >
                                        Peserta
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>
                              {idEvents ? (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  p={1}
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: "grey.50",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Tooltip title="Klik untuk tampilkan QR Code diperbesar">
                                    <MDBox
                                      onClick={() => {
                                        setQrDialogEvent(qrPayload);
                                        setOpenQrDialog(true);
                                      }}
                                      sx={{
                                        flexShrink: 0,
                                        p: 0.5,
                                        bgcolor: "white",
                                        borderRadius: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s",
                                        "&:hover": { boxShadow: 1 },
                                      }}
                                    >
                                      <QRCodeSVG value={daftarHadirUrl(idEvents)} size={44} level="M" />
                                    </MDBox>
                                  </Tooltip>
                                  <MDBox flex={1} minWidth={0} display="flex" flexDirection="column" gap={0.25}>
                                    <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                                      Daftar Hadir
                                    </MDTypography>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                      <LinkIcon sx={{ fontSize: 14, color: "text.secondary", opacity: 0.8 }} />
                                      <MDTypography
                                        variant="caption"
                                        color="primary"
                                        sx={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          cursor: "pointer",
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                        onClick={() => window.open(daftarHadirUrl(idEvents), "_blank")}
                                      >
                                        Buka link
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                </MDBox>
                              ) : (
                                <MDBox p={1} sx={{ borderRadius: 1, bgcolor: "grey.100", border: "1px solid", borderColor: "divider" }}>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Daftar hadir: ID event tidak tersedia
                                  </MDTypography>
                                </MDBox>
                              )}
                              <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} pt={1} sx={{ borderTop: 1, borderColor: "divider", mt: "auto" }}>
                                <MDBox display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                                  <MDBox position="relative" sx={{ width: 28, height: 28, flexShrink: 0 }}>
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        position: "absolute",
                                        inset: 0,
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: 18 }} />
                                    </MDBox>
                                    {detailEvent.uid_RSS_pembuat && (
                                      <MDBox
                                        component="img"
                                        src={`https://smartdh.rotendaokab.go.id/uploads/${detailEvent.uid_RSS_pembuat}.jpg`}
                                        alt=""
                                        sx={{
                                          position: "absolute",
                                          inset: 0,
                                          width: 28,
                                          height: 28,
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                        }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      />
                                    )}
                                  </MDBox>
                                  <MDTypography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detailEvent.nama_pembuat || "-"}
                                  </MDTypography>
                                </MDBox>
                                {hasLaporan ? (
                                  <Tooltip title="Lihat laporan">
                                    <IconButton
                                      color="success"
                                      size="small"
                                      onClick={() => handleOpenViewDialog(detailEvent)}
                                      sx={{ bgcolor: "action.hover", flexShrink: 0 }}
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <MDTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0, alignSelf: "center" }}>
                                    —
                                  </MDTypography>
                                )}
                              </MDBox>
                            </MDBox>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog View Notulen dan Foto - PDF */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Lihat Laporan Event (PDF)</MDTypography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {pdfLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
                <MDTypography variant="body2" color="text" ml={2}>
                  Membuat PDF...
                </MDTypography>
              </MDBox>
            ) : pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat PDF",
                    button: "OK",
                  });
                }}
              />
            ) : null}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {pdfBlobUrl && (
            <Button
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfBlobUrl;
                link.download = `Laporan_Event_${
                  viewingDetailEvent?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
              }}
              variant="outlined"
              sx={{
                color: "#000000 !important",
                borderColor: "#000000 !important",
                backgroundColor: "#ffffff",
                "&:hover": {
                  borderColor: "#000000 !important",
                  backgroundColor: "#f5f5f5",
                  color: "#000000 !important",
                },
              }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Peserta */}
      <Dialog
        open={openPesertaDialog}
        onClose={handleClosePesertaDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Peserta</MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  downloadingPesertaPdf ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )
                }
                onClick={handleDownloadPesertaPDF}
                disabled={!daftarPeserta?.length || downloadingPesertaPdf}
                sx={{
                  textTransform: "none",
                  color: "#000000 !important",
                  borderColor: "#000000 !important",
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    borderColor: "#000000 !important",
                    backgroundColor: "#f5f5f5",
                    color: "#000000 !important",
                  },
                }}
              >
                Download
              </Button>
              <IconButton onClick={handleClosePesertaDialog} size="small">
                <CloseIcon />
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingPeserta ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : daftarPeserta.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada peserta
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Nama",
                        accessor: "nama",
                        width: "25%",
                        Cell: ({ row }) => (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {row.original.uid_RSS ? (
                              <img
                                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
                                alt={
                                  row.original.nama_user || row.original.nama
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: "grey.200",
                                  color: "grey.500",
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 20 }} />
                              </MDBox>
                            )}
                            <MDTypography variant="caption" color="text">
                              {row.original.nama_user ||
                                row.original.nama ||
                                "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Instansi",
                        accessor: "instansi",
                        width: "200px",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              width: "200px",
                              py: 1,
                            }}
                          >
                            <MDTypography variant="caption" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Waktu",
                        accessor: "waktu",
                        width: "20%",
                        Cell: ({ value }) => (
                          <MDTypography variant="caption" color="text">
                            {value ? formatDateTime(value) : "-"}
                          </MDTypography>
                        ),
                      },
                      {
                        Header: "Tanda Tangan",
                        accessor: "signature_file",
                        width: "15%",
                        Cell: ({ row }) => {
                          const sig = row.original.signature_file;
                          if (!sig)
                            return (
                              <MDTypography variant="caption" color="text.secondary">
                                -
                              </MDTypography>
                            );
                          const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
                          const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
                          return (
                            <MDBox display="flex" alignItems="center" minHeight={70}>
                              <MDBox
                                component="img"
                                src={imgUrl}
                                alt="Tanda tangan"
                                sx={{
                                  maxWidth: 140,
                                  maxHeight: 70,
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </MDBox>
                          );
                        },
                      },
                    ],
                    rows: daftarPeserta.map((peserta, index) => ({
                      no: index + 1,
                      nama: peserta.nama_user || peserta.nama,
                      instansi: peserta.instansi,
                      waktu: peserta.waktu,
                      uid_RSS: peserta.uid_RSS,
                      nama_user: peserta.nama_user,
                      signature_file: peserta.signature_file,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePesertaDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code Daftar Hadir */}
      <Dialog
        open={openQrDialog}
        onClose={() => {
          setOpenQrDialog(false);
          setQrDialogEvent(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Daftar Hadir – QR Code</MDTypography>
            <IconButton
              onClick={() => {
                setOpenQrDialog(false);
                setQrDialogEvent(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {qrDialogEvent && (
            <MDBox pt={1} pb={3} px={3}>
              <MDBox mb={3}>
                <MDTypography variant="body2" color="text" sx={{ mb: 1, fontWeight: 500 }}>
                  {qrDialogEvent.kegiatan}
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                  <Chip
                    label={getStatusEventLabel(qrDialogEvent.status_event)}
                    color={getStatusEventColor(qrDialogEvent.status_event)}
                    size="small"
                    variant="filled"
                  />
                  {qrDialogEvent.scope && (
                    <Chip
                      label={getScopeLabel(qrDialogEvent.scope)}
                      color={getScopeColor(qrDialogEvent.scope)}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <MDTypography variant="caption" color="text.secondary">
                    {qrDialogEvent.jumlah_detail_events} sesi
                  </MDTypography>
                </MDBox>
                {qrDialogEvent.latestTempat && (
                  <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <PlaceIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {qrDialogEvent.latestTempat}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                p={2}
                sx={{
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography variant="caption" color="text.secondary">
                  Scan QR Code berikut untuk mengisi daftar hadir
                </MDTypography>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <QRCodeSVG
                    value={daftarHadirUrl(qrDialogEvent.id_events)}
                    size={220}
                    level="M"
                  />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <MDTypography
                    variant="caption"
                    color="primary"
                    sx={{
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => window.open(daftarHadirUrl(qrDialogEvent.id_events), "_blank")}
                  >
                    Buka link daftar hadir
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenQrDialog(false);
              setQrDialogEvent(null);
            }}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default EventYangDiikuti;
